import { PhoneType } from "@/enums/contacts";
import { SupportedCountries } from "@/enums/internationalization";
import { GailError } from "@/types/api";
import {
  Contact,
  ContactUpload,
  ContactUploadFailureRow,
} from "@/types/contacts";
import { GetPhoneNumberForUI } from "@/utils/common/phone";

type UploadApiResult = Contact | GailError;

const DEFAULT_FAILURE_REASON = "Upload failed";

const getStringValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return String(value);
  }

  return "";
};

export const formatPhoneForDisplay = (
  value: string,
  country: SupportedCountries
): string => {
  if (!value) {
    return "";
  }

  return GetPhoneNumberForUI(value, country);
};

export const buildPreUploadFailureRow = (
  rawLead: Record<string, unknown>,
  phoneFieldKeys: string[],
  reason: string,
  country: SupportedCountries
): ContactUploadFailureRow => {
  const phoneFields = phoneFieldKeys.reduce<Record<string, string>>((acc, key) => {
    const directValue = getStringValue(rawLead[key]);
    const underscoredValue = key.endsWith("_")
      ? ""
      : getStringValue(rawLead[`${key}_`]);
    const value = directValue || underscoredValue;

    if (!value) {
      return acc;
    }

    acc[key] = formatPhoneForDisplay(value, country);
    return acc;
  }, {});

  if (Object.keys(phoneFields).length === 0) {
    Object.entries(rawLead).forEach(([key, value]) => {
      const fieldValue = getStringValue(value);
      if (!fieldValue) {
        return;
      }

      const isPhoneLikeKey = /phone|mobile|cell|tel|telephone|home|work|fax/i.test(
        key
      );
      if (!isPhoneLikeKey) {
        return;
      }

      phoneFields[key] = formatPhoneForDisplay(fieldValue, country);
    });
  }

  return {
    firstName: getStringValue(rawLead.first_name) || "N/A",
    lastName: getStringValue(rawLead.last_name),
    reason,
    phoneFields,
  };
};

export const getApiFailureReason = (result: GailError): string => {
  if (!Array.isArray(result.errors) || result.errors.length === 0) {
    return DEFAULT_FAILURE_REASON;
  }

  const firstError = result.errors[0];
  return firstError.detail || firstError.title || DEFAULT_FAILURE_REASON;
};

const phoneTypeLabel = (type: PhoneType): string => {
  if (type === PhoneType.Home) {
    return "home";
  }
  if (type === PhoneType.Work) {
    return "work";
  }
  if (type === PhoneType.Fax) {
    return "fax";
  }
  return "phone";
};

const buildPhoneFieldsFromLead = (
  lead: ContactUpload,
  country: SupportedCountries
): Record<string, string> => {
  const seenByType: Record<string, number> = {};

  return lead.phoneNumbers.reduce<Record<string, string>>((acc, phoneNumber) => {
    const label = phoneTypeLabel(phoneNumber.type);
    seenByType[label] = (seenByType[label] ?? 0) + 1;
    const suffix = seenByType[label] > 1 ? `_${seenByType[label]}` : "";
    const key = `${label}${suffix}`;
    acc[key] = formatPhoneForDisplay(phoneNumber.number, country);
    return acc;
  }, {});
};

export const buildApiFailureRows = (
  submittedLeads: ContactUpload[],
  results: UploadApiResult[],
  country: SupportedCountries
): ContactUploadFailureRow[] => {
  const rows: ContactUploadFailureRow[] = [];

  results.forEach((result, index) => {
    if ("id" in result) {
      return;
    }

    const lead = submittedLeads[index];
    rows.push({
      firstName: lead?.firstName || "N/A",
      lastName: lead?.lastName || "",
      reason: getApiFailureReason(result),
      phoneFields: lead ? buildPhoneFieldsFromLead(lead, country) : {},
    });
  });

  return rows;
};

export const mergeUploadFailureRows = (
  preUploadFailures: ContactUploadFailureRow[],
  apiFailures: ContactUploadFailureRow[]
): ContactUploadFailureRow[] => [...preUploadFailures, ...apiFailures];

export const getPhoneColumnsForFailureRows = (
  rows: ContactUploadFailureRow[]
): string[] => {
  const columnSet = new Set<string>();

  rows.forEach((row) => {
    Object.keys(row.phoneFields).forEach((key) => columnSet.add(key));
  });

  return Array.from(columnSet);
};
